/**
 * 커뮤니티 글 상세 보기 페이지
 * 글 내용, 댓글 목록, 댓글 작성 기능 포함
 */

import React, { useState, useEffect, useId, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { communityApi, getRelativeTime, getErrorMessage } from '../../api/communityApi';
import LoadingSpinner from '../../components/layout/LoadingSpinner';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/common/ConfirmDialog';
import Dialog from '../../components/common/Dialog';
import ModalActions from '../../components/common/ModalActions';
import './PostDetailPage.css';

const CLOSE_PASSWORD_MODAL_CONFIRM = {
  title: '창을 닫을까요?',
  description: '입력한 비밀번호는 지워져요',
  confirmLabel: '닫기',
};

/**
 * 비밀번호 확인 삭제 대화상자 — 글과 댓글이 같은 흐름을 공유한다.
 * 익명 커뮤니티라 작성할 때 정한 비밀번호로만 본인 확인이 되므로, 예/아니오만 돌려주는 useConfirm()으로는
 * 대신할 수 없다. onDelete(password)가 실제 삭제 API·토스트·이동을 맡고, 실패 토스트는 여기서 띄운다.
 */
const DeleteWithPasswordDialog = ({
  open,
  title,
  description,
  passwordLabel,
  closeLabel,
  testId,
  onClose,
  onDelete,
}) => {
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);
  const inputId = useId();
  const errorId = useId();
  const { showToast } = useToast();
  const openConfirm = useConfirm();

  // 닫히면 입력값과 오류를 비운다 (다음에 열 때 깨끗한 상태로 시작)
  useEffect(() => {
    if (!open) {
      setPassword('');
      setPasswordError('');
    }
  }, [open]);

  const handleClose = async () => {
    if (deleting) return;
    if (password.trim() && !(await openConfirm(CLOSE_PASSWORD_MODAL_CONFIRM))) return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!password.trim()) {
      setPasswordError('삭제할 비밀번호를 입력해주세요');
      return;
    }

    setDeleting(true);
    setPasswordError('');
    try {
      await onDelete(password);
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      closeLabel={closeLabel}
      description={description}
      dismissible={!deleting}
      initialFocus={inputRef}
      onClose={handleClose}
      open={open}
      role="alertdialog"
      size="sm"
      testId={testId}
      title={title}
    >
      <form className="community-delete-dialog" noValidate onSubmit={handleSubmit}>
        <label className="community-delete-dialog__label" htmlFor={inputId}>{passwordLabel}</label>
        <input
          aria-describedby={passwordError ? errorId : undefined}
          aria-invalid={passwordError ? 'true' : undefined}
          autoComplete="current-password"
          className={`community-delete-dialog__input${passwordError ? ' community-delete-dialog__input--error' : ''}`}
          disabled={deleting}
          id={inputId}
          onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
          placeholder="비밀번호를 입력해주세요"
          ref={inputRef}
          type="password"
          value={password}
        />
        {passwordError && (
          <p className="community-delete-dialog__error" id={errorId} role="alert">{passwordError}</p>
        )}
        <ModalActions
          className="community-delete-dialog__actions"
          primary={{
            label: '삭제하기',
            type: 'submit',
            danger: true,
            loading: deleting,
            disabled: !password.trim(),
          }}
          secondary={{ label: '취소', onClick: handleClose }}
          testId={`${testId}-actions`}
        />
      </form>
    </Dialog>
  );
};

const CommentItem = ({ comment, loading, onCommentDeleted, postId }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { showToast } = useToast();

  // 삭제 API·성공 토스트·목록 새로고침은 그대로. 실패 토스트는 대화상자가 띄운다.
  const handleDeleteComment = async (password) => {
    await communityApi.deleteComment(postId, comment.id, password);
    showToast('댓글을 삭제했어요', 'success');
    setShowDeleteModal(false);
    onCommentDeleted && onCommentDeleted();
  };

  return (
    <>
      <div className={`comment-item ${loading ? 'loading' : ''}`}>
        <div className="comment-header">
          <div className="comment-info">
            <span className="comment-author">
              {comment.author_name || '익명'}
            </span>
            <span className="separator">•</span>
            <span className="comment-time">
              {getRelativeTime(comment.created_at)}
            </span>
          </div>
          
          <button 
            className="comment-delete-btn"
            onClick={() => setShowDeleteModal(true)}
            title="댓글 삭제"
          >
            삭제
          </button>
        </div>
        
        <div className="comment-content">
          <p>{comment.content}</p>
        </div>
      </div>

      {/* 댓글 삭제 — 글 삭제와 같은 비밀번호 확인 대화상자 */}
      <DeleteWithPasswordDialog
        closeLabel="댓글 삭제 창 닫기"
        description="삭제한 댓글은 되돌릴 수 없어요"
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteComment}
        open={showDeleteModal}
        passwordLabel="댓글 작성 시 입력한 비밀번호"
        testId="comment-delete-dialog"
        title="이 댓글을 삭제할까요?"
      />
    </>
  );
};

const CommentForm = ({ postId, onCommentCreated, onCancel }) => {
  const [content, setContent] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setError('댓글 내용을 입력해주세요.');
      return;
    }

    if (content.trim().length > 500) {
      setError('댓글은 500자 이하로 작성해주세요.');
      return;
    }

    if (!password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await communityApi.createComment(postId, content, password);
      setContent('');
      setPassword('');
      onCommentCreated && onCommentCreated();
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="comment-form">
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="댓글을 작성해주세요... (최대 500자)"
            className={`comment-textarea ${error ? 'error' : ''}`}
            rows={3}
            maxLength={500}
            disabled={submitting}
          />
          
          <div className="password-input">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="수정/삭제용 비밀번호 (4-12자)"
              className={`password-field ${error ? 'error' : ''}`}
              maxLength={12}
              disabled={submitting}
            />
            <div className="password-hint">
              <span>이 비밀번호로 나중에 댓글을 수정/삭제할 수 있습니다.</span>
            </div>
          </div>
          
          <div className="form-footer">
            <div className="char-count">
              <span className={content.length > 450 ? 'warning' : ''}>
                {content.length}/500
              </span>
            </div>
            
            <div className="form-buttons">
              {onCancel && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  취소
                </button>
              )}
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting || !content.trim() || !password.trim()}
              >
                {submitting ? (
                  <>
                    댓글 작성 중...
                  </>
                ) : (
                  <>
                    댓글 작성
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        {error && (
          <div className="error-message">
            <span>{error}</span>
          </div>
        )}
      </form>
    </div>
  );
};

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  // const { user } = useAuth(); // 익명 커뮤니티이므로 사용하지 않음
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { showToast } = useToast();

  // 컴포넌트 마운트 시 글 조회
  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;
      
      setLoading(true);
      setError('');

      try {
        const postData = await communityApi.getPost(postId);
        setPost(postData);
      } catch (error) {
        console.error('글 조회 실패:', error);
        setError(getErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [postId]);

  // 댓글 작성 완료 핸들러
  const handleCommentCreated = () => {
    setShowCommentForm(false);
    refreshPost(); // 새로고침하여 새 댓글 반영
  };

  // 댓글 삭제 완료 핸들러
  const handleCommentDeleted = () => {
    refreshPost(); // 새로고침하여 삭제된 댓글 반영
  };

  // 글 새로고침 (로딩 스피너 없이)
  const refreshPost = async () => {
    if (!postId) return;
    
    setRefreshing(true);
    setError('');

    try {
      const postData = await communityApi.getPost(postId);
      setPost(postData);
    } catch (error) {
      console.error('글 조회 실패:', error);
      setError(getErrorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };

  // 댓글 작성 버튼 클릭 (익명 커뮤니티이므로 로그인 불필요)
  const handleCreateCommentClick = () => {
    setShowCommentForm(true);
  };

  // 글 삭제 핸들러 — 삭제 API·성공 토스트·목록 이동은 그대로. 실패 토스트는 대화상자가 띄운다.
  const handleDeletePost = async (password) => {
    await communityApi.deletePost(postId, password);
    showToast('글을 삭제했어요', 'success');
    navigate('/community');
  };

  // 뒤로가기
  const handleBackClick = () => {
    navigate('/community');
  };

  // 새로고침 기능 제거됨

  // 로딩 중
  if (loading) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-header">
          <button className="back-btn" onClick={handleBackClick}>
            목록으로
          </button>
        </div>
        <div className="loading-container">
          <LoadingSpinner />
          <p>글을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error && !post) {
    return (
      <div className="post-detail-page">
        <div className="post-detail-header">
          <button className="back-btn" onClick={handleBackClick}>
            목록으로
          </button>
        </div>
        <div className="error-container">
          <div className="error-message large">
            <div className="error-content">
              <h3>글을 불러오지 못했어요</h3>
              <p>{error}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                다시 시도
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      {/* 헤더 */}
      <div className="post-detail-header">
        <button className="back-btn" onClick={handleBackClick}>
          목록으로
        </button>
      </div>

      {post && (
        <>
          {/* 글 본문 */}
          <div className="post-detail-content">
            <article className="post-article">
              {/* 글 헤더 */}
              <header className="post-header">
                <div className="post-title-row">
                  <h1 className="post-title">{post.title}</h1>
                  <button 
                    className="delete-post-btn"
                    onClick={() => setShowDeleteModal(true)}
                    title="글 삭제"
                  >
                    삭제
                  </button>
                </div>
                
                <div className="post-meta">
                  <div className="author-info">
                    <span className="author">
                      {post.author_name || '익명'}
                    </span>
                  </div>
                  
                  <div className="post-info">
                    <span className="post-time">
                      {getRelativeTime(post.created_at)}
                    </span>
                  </div>
                </div>

                {/* 글 통계 */}
                <div className="post-stats">
                  <div className="stat-item">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span className="count">{post.view_count || 0}</span>
                  </div>
                  <div className="stat-item">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                    <span className="count">{post.comment_count || 0}</span>
                  </div>
                </div>
              </header>

              {/* 글 내용 */}
              <div className="post-body">
                <div className="content">
                  {post.content.split('\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </article>
          </div>

          {/* 댓글 섹션 */}
          <div className="comments-section">
            <div className="comments-header">
              <h3>
                댓글 {post.comments ? post.comments.length : 0}개
              </h3>
              
              <button
                className="btn btn-primary comment-write-btn"
                onClick={handleCreateCommentClick}
              >
                댓글 작성
              </button>
            </div>

            {/* 댓글 작성 폼 */}
            {showCommentForm && (
              <div className="comment-form-container">
                <CommentForm
                  postId={postId}
                  onCommentCreated={handleCommentCreated}
                  onCancel={() => setShowCommentForm(false)}
                />
              </div>
            )}

            {/* 댓글 목록 */}
            <div className="comments-list">
              {post.comments && post.comments.length > 0 ? (
                post.comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    comment={comment}
                    loading={refreshing}
                    onCommentDeleted={handleCommentDeleted}
                    postId={postId}
                  />
                ))
              ) : (
                <div className="empty-comments">
                  <h4>아직 댓글이 없어요</h4>
                  <p>첫 번째 댓글을 남겨보세요</p>
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateCommentClick}
                  >
                    첫 댓글 작성하기
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* 글 삭제 — 댓글 삭제와 같은 비밀번호 확인 대화상자 */}
      <DeleteWithPasswordDialog
        closeLabel="게시글 삭제 창 닫기"
        description="삭제한 글은 되돌릴 수 없어요"
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeletePost}
        open={showDeleteModal}
        passwordLabel="글 작성 시 입력한 비밀번호"
        testId="post-delete-dialog"
        title="이 글을 삭제할까요?"
      />
    </div>
  );
};

export default PostDetailPage; 